/**
 * Seattle Metro Solo マップ設定
 * Seattle Metro Solo 専用の設定値を定義
 */

import type { MapConfig, TileType, SeattleDiceGroup, GoodsColor } from '../../../src/types';

// ============================================================================
// マップ基本設定
// ============================================================================

/**
 * Seattle Metro Solo マップ設定
 */
export const SEATTLE_MAP_CONFIG: MapConfig = {
  name: 'Seattle Metro Solo',
  // ソロルールでは全新都市タイルを除外
  availableNewCityTiles: [] as TileType[],
  defaultPlayerCount: 1,
  maxTurns: 10,
  maxSharesPerTurn: undefined,  // 毎ターンの発行上限なし
  hasGoodsDisplay: false,  // 商品ディスプレイ不使用
  hasEngineerBonus: false,  // エンジニアボーナスなし（標準ルール）
  expenseFormula: 'standard',  // 株式 + 機関車Lv（貨物輸送Lv維持費は別途追加）
  incomeReductionFormula: 'standard',  // 標準ルール（10ステップごと）
  isSoloPlay: true,
  canBuildOnMountains: false,  // 山岳ヘクスへのタイル敷設禁止
  maxSharesLimit: 15,  // 株式発行上限15枚
  cargoTransportExpenses: { 2: 0, 3: 4, 4: 7, 5: 10 },  // 貨物輸送レベル維持費
  grayCityIds: ['DOWNTOWN', 'AIRPORT'],  // 収入0の特殊都市
};

// ============================================================================
// 商品設定
// ============================================================================

/**
 * Seattle 商品キューブ設定
 * - 全5色使用（標準色）
 */
export const SEATTLE_CUBES_CONFIG = {
  cubesPerColor: {
    red: 20,
    blue: 20,
    purple: 20,
    yellow: 20,
    black: 20
  },
  excludeColors: [] as GoodsColor[]  // 全色使用
};

// ============================================================================
// ゲーム特有ルール
// ============================================================================

/**
 * Seattle Metro Solo 固有ルール
 */
export const SEATTLE_RULES = {
  /** 初期資金 */
  initialCash: 10,
  /** 初期株式数 */
  initialShares: 2,
  /** 初期機関車レベル */
  initialLocoLevel: 1,
  /** 株式発行上限 */
  maxSharesLimit: 15,
  /** 貨物輸送レベル初期値 */
  initialCargoTransportLevel: 2,
  /** 貨物輸送レベル最大値 */
  maxCargoTransportLevel: 5,
  /** 貨物輸送レベル維持費テーブル */
  cargoTransportExpenses: {
    2: 0,
    3: 4,
    4: 7,
    5: 10
  } as Record<number, number>,
  /** City/Town への最大商品数（需要過多判定用） */
  maxGoodsPerLocation: 2,
  /** Airport への最大商品数 */
  airportGoodsCapacity: 3,
  /** アクション使用料（清算時） */
  actionUsageCost: 5,
  /** 株式買い戻し価格（清算時） */
  shareBuybackPrice: 5,
  /** Downtown/Airport に輸送し取り除いた商品キューブ1個あたりの得点（ソロルール） */
  deliveredCubePoints: 1,
  /** ソロプレイ専用 */
  soloPlayOnly: true
} as const;

/**
 * Seattle ダイス出目グループ（ルールに基づく）
 *
 * | 出目 | 対応する City/Town |
 * | ---- | ------------------ |
 * | 1    | BALLARD, CROWN HILL |
 * | 2    | LAKE CITY, WALLINGFORD |
 * | 3    | SOUTH LAKE UNION, DISCOVERY PARK, FREMONT |
 * | 4    | UNIVERSITY DISTRICT, CAPITOL HILL, ISLAND CREST |
 * | 5    | ALKI BEACH, SEATTLE CENTER |
 * | 6    | MERCER ISLAND, COLUMBIA CITY, RENTON |
 *
 * 注: Downtown/Airport は出目5,6でも配置不可（ルール記載）
 */
export const SEATTLE_DICE_GROUPS: SeattleDiceGroup[] = [
  {
    diceValue: 1,
    locationIds: ['BALLARD', 'CROWN_HILL']
  },
  {
    diceValue: 2,
    locationIds: ['LAKE_CITY', 'WALLINGFORD']
  },
  {
    diceValue: 3,
    locationIds: ['SOUTH_LAKE_UNION', 'DISCOVERY_PARK', 'FREMONT']
  },
  {
    diceValue: 4,
    locationIds: ['UNIVERSITY_DISTRICT', 'CAPITOL_HILL', 'ISLAND_CREST']
  },
  {
    diceValue: 5,
    locationIds: ['ALKI_BEACH', 'SEATTLE_CENTER']
  },
  {
    diceValue: 6,
    locationIds: ['MERCER_ISLAND', 'COLUMBIA_CITY', 'RENTON']
  }
];

/**
 * Gray Cities（Downtown/Airport）のID
 * - 何色の商品でも輸送可能
 * - 収入0
 * - 留置可能
 */
export const SEATTLE_GRAY_CITIES = {
  DOWNTOWN: 'DOWNTOWN',
  AIRPORT: 'AIRPORT'
} as const;

// ============================================================================
// UI設定
// ============================================================================

/**
 * Seattle GameStartModal 設定
 *
 * 共通キーは gameStartCommon から取得、マップ固有キーは gameStartSeattle から取得。
 */
export const SEATTLE_START_MODAL_CONFIG = {
  /** i18n マップ固有キープレフィックス */
  i18nKey: 'gameStartSeattle',
  /** 勝利条件セクションのスタイル */
  victoryStyle: 'success' as const,
  /** 勝利/終了条件設定（統一構造） */
  victory: {
    gameEndKey: 'gameEnd',  // 共通キー
    winConditionKey: 'winCondition',  // マップ固有キー
    additionalItems: ['settlementPayment', 'scoreCalculation'],  // マップ固有キー
  },
  /** 特殊ルールフェーズ */
  phases: [
    { num: '3', ruleKey: 'selectActionsRule', descKey: 'selectActionsDesc', actionsKey: 'selectActionsActions', extraKeys: ['locomotiveChoice', 'actionTracking'] },
    { num: '5', ruleKey: 'moveGoodsRule', descKey: 'moveGoodsDesc', extraKeys: ['grayCities', 'deliverOrKeep'] },
    { num: '7', descKey: 'payExpensesDesc' },
    { num: '9', descKey: 'refillGoodsDesc', extraKeys: ['demandOverload'] },
  ],
  /** 戦略ヒント */
  tips: ['tip1', 'tip2', 'tip3', 'tip4'],
  /** 特殊リンク */
  specialLinks: ['preLaidTrack', 'wallsDesc', 'mountainDesc'],
};

/**
 * Seattle GameEndScreen 設定
 */
export const SEATTLE_END_SCREEN_CONFIG = {
  /** スコア計算タイプ */
  scoreType: 'finalScore' as const,
  /** 勝利判定（スコア0以上で勝利） */
  victoryThreshold: 0,
  /** スコア評価（降順で検索、最初にマッチしたものを使用） */
  scoreEvaluations: [
    { minScore: 10, key: 'excellent' },
    { minScore: 5, key: 'good' },
    { minScore: 0, key: 'passing' },
    { minScore: -10, key: 'poor' },
    { minScore: -Infinity, key: 'veryPoor' },
  ],
};
